import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { Loader2, Undo2, Activity, LogIn, RefreshCw } from 'lucide-react';

const METHOD_COLORS = {
  POST: 'bg-emerald-100 text-emerald-800',
  PUT: 'bg-amber-100 text-amber-800',
  PATCH: 'bg-amber-100 text-amber-800',
  DELETE: 'bg-red-100 text-red-700',
};

const ActivityLogAdmin = () => {
  const [tab, setTab] = useState('audit');    // 'audit' | 'logins'
  const [audit, setAudit] = useState([]);
  const [logins, setLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterActor, setFilterActor] = useState('');
  const [filterMethod, setFilterMethod] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'audit') {
        const params = {};
        if (filterActor) params.actor = filterActor;
        if (filterMethod) params.action = filterMethod;
        const res = await api.get('/admin/audit-log', { params });
        setAudit(res.data);
      } else {
        const res = await api.get('/admin/login-history');
        setLogins(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load activity');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const revert = async (id) => {
    if (!window.confirm('Revert this action? This restores the previous value where possible.')) return;
    try {
      await api.post(`/admin/audit-log/${id}/revert`);
      toast.success('Reverted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="activity-log-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-black text-[#12263A] flex items-center gap-2"><Activity className="w-5 h-5 text-[#F46A25]" /> Activity Log</h2>
          <p className="text-xs text-neutral-600 mt-1 max-w-xl">
            Every admin action is tracked — who, what, when, and from which IP. Login history is retained for 90 days.
          </p>
        </div>
        <button onClick={load} className="text-xs border border-neutral-300 hover:border-[#F46A25] px-3 py-1.5 rounded-sm flex items-center gap-1" data-testid="activity-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="flex border-b border-neutral-200">
        <button onClick={() => setTab('audit')} data-testid="activity-tab-audit" className={`px-4 py-2 text-sm font-bold ${tab === 'audit' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-500 hover:text-[#12263A]'}`}>
          <Activity className="w-4 h-4 inline mr-1" /> Actions
        </button>
        <button onClick={() => setTab('logins')} data-testid="activity-tab-logins" className={`px-4 py-2 text-sm font-bold ${tab === 'logins' ? 'text-[#F46A25] border-b-2 border-[#F46A25]' : 'text-neutral-500 hover:text-[#12263A]'}`}>
          <LogIn className="w-4 h-4 inline mr-1" /> Login history
        </button>
      </div>

      {tab === 'audit' && (
        <div className="flex flex-wrap items-end gap-2 pb-2">
          <label className="block">
            <span className="text-[10px] uppercase text-neutral-500 font-bold">Actor email</span>
            <input value={filterActor} onChange={e => setFilterActor(e.target.value)} placeholder="all admins" className="block border border-neutral-300 rounded-sm px-2 py-1 text-xs mt-0.5 w-56" data-testid="audit-filter-actor" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase text-neutral-500 font-bold">Method</span>
            <select value={filterMethod} onChange={e => setFilterMethod(e.target.value)} className="block border border-neutral-300 rounded-sm px-2 py-1 text-xs mt-0.5" data-testid="audit-filter-method">
              <option value="">All</option><option>POST</option><option>PUT</option><option>PATCH</option><option>DELETE</option>
            </select>
          </label>
          <button onClick={load} className="bg-[#12263A] hover:bg-[#F46A25] text-white text-xs font-bold px-3 py-1.5 rounded-sm">Apply</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F46A25]" /></div>
      ) : tab === 'audit' ? (
        <div className="border border-neutral-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase text-neutral-600 font-bold">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Actor</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Resource</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Revert</th>
              </tr>
            </thead>
            <tbody>
              {audit.map(a => (
                <tr key={a.id} className="border-t border-neutral-100" data-testid={`audit-row-${a.id}`}>
                  <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">{new Date(a.timestamp).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{a.actor_email || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm mr-1 ${METHOD_COLORS[a.method] || 'bg-neutral-100 text-neutral-700'}`}>{a.method}</span>
                    <span className="font-mono text-[11px]">{a.path}</span>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {a.collection || a.resource_type || '—'}{a.resource_id ? <span className="text-neutral-400"> · {a.resource_id.slice(0, 8)}</span> : ''}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">{a.ip || '—'}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${a.status_code < 300 ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`}>{a.status_code}</span>
                    {a.reverted && <span className="ml-1 inline-block text-[10px] font-bold bg-neutral-200 text-neutral-700 px-1.5 py-0.5 rounded-sm">reverted</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {a.before && a.collection && !a.reverted ? (
                      <button onClick={() => revert(a.id)} data-testid={`revert-btn-${a.id}`} className="text-[11px] text-[#F46A25] hover:text-[#12263A] font-bold flex items-center gap-1 ml-auto">
                        <Undo2 className="w-3 h-3" /> Revert
                      </button>
                    ) : (
                      <span className="text-[10px] text-neutral-300 italic">—</span>
                    )}
                  </td>
                </tr>
              ))}
              {audit.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-neutral-400 italic">No actions logged yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase text-neutral-600 font-bold">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">User agent</th>
                <th className="text-left px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {logins.map(l => (
                <tr key={l.id} className="border-t border-neutral-100" data-testid={`login-row-${l.id}`}>
                  <td className="px-3 py-2 text-neutral-600 whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">{l.user_email || '—'}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">{l.ip || '—'}</td>
                  <td className="px-3 py-2 text-neutral-500 max-w-[300px] truncate" title={l.user_agent}>{l.user_agent || '—'}</td>
                  <td className="px-3 py-2">
                    {l.success
                      ? <span className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800">Success</span>
                      : <span className="inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700" title={l.reason}>Failed{l.reason ? ` · ${l.reason}` : ''}</span>}
                  </td>
                </tr>
              ))}
              {logins.length === 0 && (
                <tr><td colSpan={5} className="text-center py-12 text-neutral-400 italic">No logins yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ActivityLogAdmin;
